import { useRef, useState } from 'react'
import ConfirmDialog from '../components/ConfirmDialog'
import FancyNotePicker, { useFancyNotes } from '../components/FancyNotePicker'
import SpaceForm from '../components/SpaceForm'
import { api, fieldErrorsFromDetail } from '../lib/api'
import { dateInputValue } from '../lib/format'

// A space's Settings tab — "Edit" on the space page, after the admin screens
// proposal: the three numbered sections of the space form
// (components/SpaceForm.jsx), with the invitation's wording as section 3,
// beside an aside that says what is unsaved and saves it, replaces the brief
// from a fancy-note, and archives the space. Saves PATCH only what changed.
// Share and revoke live in the header's access card, not here.
//
// `space` is the loaded row; `onChanged` hands back the server's version after
// a save so the header above stays true; `onRequestShare` opens the header's
// share confirm — offered after the end date moves.
//
// Picking a fancy-note asks the server for the brief Claude writes from it
// (POST /api/v1/fancy-notes/<id>/brief — stored nowhere) and puts it in the
// brief field: a draft edit like typing, kept by Save changes and gone on a
// refresh. The dropdown keeps the pick until then. A brief that has anything
// in it asks first, since the pick overwrites it. The card is hidden when the
// integration is off. (The New space dialog uses the storing import instead.)

const PLACEHOLDERS = [
  '{FIRST_NAME}',
  '{PRINCIPAL_NAME}',
  '{CLIENT_NAME}',
  '{PURPOSE}',
  '{LINK}',
  '{EXPIRY_DATE}',
]

// The editable fields, as the form holds them: strings for inputs, a boolean
// for the switch. Nulls from the API become '' so a blank stays a blank.
function fromSpace(space) {
  return {
    client_name: space.client_name ?? '',
    engagement: space.engagement ?? '',
    ends_at: dateInputValue(space.ends_at),
    purpose: space.purpose ?? '',
    brief: space.brief ?? '',
    principal_name: space.principal_name ?? '',
    principal_title: space.principal_title ?? '',
    principal_email: space.principal_email ?? '',
    notify_principal: Boolean(space.notify_principal),
    sender_name: space.sender_name ?? '',
    // The admin's wording. The live draft (email_subject / email_body) is the
    // principal's to edit on her page and is not touched here.
    invite_base_subject: space.invite_base_subject ?? '',
    invite_base_body: space.invite_base_body ?? '',
    // One default per template variable; a missing key means the engagement.
    variable_defaults: { ...(space.variable_defaults ?? {}) },
    hidden_variables: [...(space.hidden_variables ?? [])],
  }
}

// Field-by-field equality: the defaults are an object, rebuilt on every
// render, so a reference check would call them changed when nothing was.
function same(a, b) {
  return typeof a === 'object' && a !== null ? JSON.stringify(a) === JSON.stringify(b) : a === b
}

// What PATCH may carry. The template is fixed: shown read-only by the form
// and never sent. The principal's email is sent when it changes; the server
// moves the space to that address and closes the old one's access.
const PATCHABLE = new Set([
  'client_name',
  'engagement',
  'ends_at',
  'purpose',
  'brief',
  'principal_name',
  'principal_title',
  'principal_email',
  'notify_principal',
  'sender_name',
  'invite_base_subject',
  'invite_base_body',
  'variable_defaults',
  'hidden_variables',
])

export default function SpaceSettingsPage({ space, onChanged, onRequestShare }) {
  const [draft, setDraft] = useState(() => fromSpace(space))
  const [fieldErrors, setFieldErrors] = useState({})
  const [flash, setFlash] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const fancy = useFancyNotes()
  const [pickedNote, setPickedNote] = useState('') // the dropdown's value while a pick is pending
  const [pendingNote, setPendingNote] = useState(null) // a note waiting on "Replace the brief?"
  const [importing, setImporting] = useState(false)
  const bodyRef = useRef(null)

  const saved = fromSpace(space)
  const changed = Object.fromEntries(
    Object.entries(draft).filter(
      ([field, value]) => PATCHABLE.has(field) && !same(value, saved[field]),
    ),
  )
  const changedCount = Object.keys(changed).length
  const dirty = changedCount > 0

  function set(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  // Drops the token at the caret of the body textarea (or at the end when the
  // field has not been focused yet), then puts the caret after it.
  function insertPlaceholder(token) {
    const element = bodyRef.current
    const start = element?.selectionStart ?? draft.invite_base_body.length
    const end = element?.selectionEnd ?? start
    const next = draft.invite_base_body.slice(0, start) + token + draft.invite_base_body.slice(end)
    set('invite_base_body', next)
    requestAnimationFrame(() => {
      if (!element) return
      element.focus()
      element.setSelectionRange(start + token.length, start + token.length)
    })
  }

  async function handleSave(event) {
    event.preventDefault()
    if (!dirty || saving) return

    const errors = {}
    if (!draft.client_name.trim()) errors.client_name = 'Client name is required.'
    if (!draft.engagement.trim()) errors.engagement = 'Engagement is required.'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.ends_at)) errors.ends_at = 'Pick a valid date.'
    if (!draft.principal_name.trim()) errors.principal_name = 'Name is required.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.principal_email.trim()))
      errors.principal_email = 'Enter a valid email address.'
    if (!draft.sender_name.trim()) errors.sender_name = 'Required.'
    if (!draft.invite_base_subject.trim()) errors.email_subject = 'Required.'
    if (!draft.invite_base_body.trim()) errors.email_body = 'Required.'
    else if (!draft.invite_base_body.includes('{LINK}'))
      errors.email_body = 'The body must include {LINK} — otherwise the email has no link in it.'
    setFieldErrors(errors)
    setFlash(null)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const body = Object.fromEntries(
        Object.entries(changed).map(([field, value]) => [
          field,
          typeof value === 'string' ? value.trim() : value,
        ]),
      )
      const updated = await api(`/api/v1/spaces/${space.id}`, { method: 'PATCH', body })
      setDraft(fromSpace(updated))
      onChanged(updated)
      if ('principal_email' in changed && space.principal_email !== updated.principal_email) {
        // The server already closed the old address's access; the new one has
        // no link until the space is shared with them, so offer that here.
        setFlash({
          type: 'success',
          text: `Changes saved. ${space.principal_email} no longer has access; share the space to send ${updated.principal_email} their link.`,
          action: onRequestShare ? { label: 'Share', onClick: onRequestShare } : undefined,
        })
      } else if ('ends_at' in changed && updated.access?.status === 'active' && onRequestShare) {
        // A moved end date already moved the principal's link with it, so the
        // one in their inbox keeps working; the offer is a courtesy, not a repair.
        setFlash({
          type: 'success',
          text: `Changes saved. ${updated.principal_name}’s link now runs to the new end date.`,
          action: { label: 'Send link again', onClick: onRequestShare },
        })
      } else {
        setFlash({ type: 'success', text: 'Changes saved.' })
      }
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      if (mapped) setFieldErrors(mapped)
      else setFlash({ type: 'error', text: err.detail || 'Could not save these changes.' })
    } finally {
      setSaving(false)
    }
  }

  // Restores subject and body from the template. The sender name is the
  // space's own and stays. Nothing is saved until Save changes.
  async function handleReset() {
    setResetting(true)
    try {
      const template = await api(`/api/v1/templates/${space.template_id}`)
      setDraft((current) => ({
        ...current,
        invite_base_subject: template.email_subject,
        invite_base_body: template.email_body,
      }))
      setFlash(null)
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || 'Could not load the template.' })
    } finally {
      setResetting(false)
      setConfirmReset(false)
    }
  }

  // Picking a note fills the brief field — after a confirm when the field
  // holds anything, typed or saved, because the pick overwrites it. Nothing
  // reaches the server's brief until Save; "None — start blank" empties the
  // field the same way.
  function handlePickNote(note) {
    setPickedNote(note?.id ?? '')
    if (!note) {
      setDraft((current) => ({ ...current, brief: '' }))
      return
    }
    if (draft.brief.trim()) setPendingNote(note)
    else importNote(note)
  }

  async function importNote(note) {
    setImporting(true)
    setFlash(null)
    try {
      const written = await api(`/api/v1/fancy-notes/${note.id}/brief`, { method: 'POST' })
      // Only the brief field moves, and only here: it is saved with the rest
      // of the admin's edits, or dropped with them.
      setDraft((current) => ({ ...current, brief: written.brief ?? '' }))
      setFlash({
        type: 'success',
        text: `Brief written from “${note.title}” — save changes to keep it.`,
      })
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || 'Could not write a brief from that note.' })
      setPickedNote('')
    } finally {
      setImporting(false)
      setPendingNote(null)
    }
  }

  async function handleArchive() {
    const archived = !space.archived_at
    setArchiving(true)
    setFlash(null)
    try {
      const out = await api(`/api/v1/spaces/${space.id}/archived`, {
        method: 'PUT',
        body: { archived },
      })
      onChanged({ ...space, archived_at: out.archived_at })
      setFlash({
        type: 'success',
        text: archived ? 'Space archived.' : 'Space moved back to the active list.',
      })
    } catch (err) {
      setFlash({
        type: 'error',
        text: err.detail || `Could not ${archived ? 'archive' : 'unarchive'} this space.`,
      })
    } finally {
      setArchiving(false)
      setConfirmArchive(false)
    }
  }

  function fieldError(name) {
    return fieldErrors[name] ? (
      <span className="field__error" role="alert">
        {fieldErrors[name]}
      </span>
    ) : null
  }

  const inputClass = (name) => `field__input${fieldErrors[name] ? ' field__input--invalid' : ''}`
  const templateName = space.template_name ?? 'template'

  const invitation = (
    <>
      <div className="space-form__grid">
        <div className="field">
          <label className="field__label" htmlFor="space-sender">
            Sender name
          </label>
          <input
            id="space-sender"
            type="text"
            className={inputClass('sender_name')}
            value={draft.sender_name}
            onChange={(event) => set('sender_name', event.target.value)}
          />
          {fieldError('sender_name') || (
            <span className="field__help">The From name on every invitation.</span>
          )}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="space-subject">
            Subject
          </label>
          <input
            id="space-subject"
            type="text"
            className={inputClass('invite_base_subject')}
            value={draft.invite_base_subject}
            onChange={(event) => set('invite_base_subject', event.target.value)}
          />
          {fieldError('invite_base_subject')}
        </div>
      </div>
      <div className="field">
        <label className="field__label" htmlFor="space-body">
          Body
        </label>
        <textarea
          id="space-body"
          ref={bodyRef}
          rows={10}
          className={inputClass('invite_base_body')}
          value={draft.invite_base_body}
          onChange={(event) => set('invite_base_body', event.target.value)}
        />
        {fieldError('invite_base_body')}
      </div>
      <div className="chips chips--inline">
        <span className="field__help">Insert</span>
        {PLACEHOLDERS.map((token) => (
          <button
            key={token}
            type="button"
            className="chip-token"
            onClick={() => insertPlaceholder(token)}
          >
            {token}
          </button>
        ))}
      </div>
    </>
  )

  return (
    <form className="settings" onSubmit={handleSave} noValidate>
      {flash && (
        <div
          className={`alert alert--${flash.type} text-body-sm`}
          style={{ alignItems: 'center' }}
          role={flash.type === 'error' ? 'alert' : 'status'}
        >
          <span style={{ flex: 1 }}>{flash.text}</span>
          {flash.action && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={flash.action.onClick}>
              {flash.action.label}
            </button>
          )}
        </div>
      )}

      <div className="settings__grid">
        <div className="settings__main">
          <SpaceForm
            mode="edit"
            draft={draft}
            set={set}
            fieldErrors={fieldErrors}
            templateName={space.template_name}
            variables={space.template_variables ?? []}
            disabled={importing}
            invitations={invitation}
            invitationsMeta={
              space.invite_custom
                ? `${space.principal_name} has edited the invitation on her page; her version is what goes out. This is what Revert to original brings back.`
                : 'What every new link in this space starts from, and what the principal’s Revert to original brings back. She may edit her copy on her page.'
            }
            invitationsAction={
              <button
                type="button"
                className="text-btn"
                onClick={() => setConfirmReset(true)}
                disabled={resetting}
              >
                Reset to template
              </button>
            }
          />
        </div>

        <aside className="settings__aside">
          <section className="aside-card" aria-labelledby="settings-changes">
            <p id="settings-changes" className="eyebrow-sm">
              Changes
            </p>
            <p className="aside-card__text">
              {dirty
                ? `${changedCount} unsaved change${changedCount === 1 ? '' : 's'}.`
                : 'No unsaved changes.'}
            </p>
            <button type="submit" className="btn btn--primary" disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </section>

          {fancy.status !== 'off' && (
            <section className="aside-card aside-card--alt" aria-labelledby="settings-note">
              <p id="settings-note" className="eyebrow-sm">
                Start from a fancy-note
              </p>
              <FancyNotePicker
                id="space-brief-note"
                label="Replace the brief with"
                fancy={fancy}
                value={pickedNote}
                onPick={handlePickNote}
                disabled={importing}
                help="Claude writes a short brief from the note into the field; “None — start blank” empties it. Save changes keeps either."
              />
            </section>
          )}

          <section className="aside-card" aria-labelledby="settings-archive">
            <p id="settings-archive" className="eyebrow-sm">
              {space.archived_at ? 'Archived' : 'Archive'}
            </p>
            <p className="aside-card__text aside-card__text--sm">
              {space.archived_at
                ? 'In the Archived list. Nothing can be sent and the principal’s link is closed until it is unarchived.'
                : 'Moves to the Archived list. Links and results are kept.'}
            </p>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setConfirmArchive(true)}
              disabled={archiving}
            >
              {space.archived_at ? 'Unarchive space' : 'Archive space'}
            </button>
          </section>
        </aside>
      </div>

      {confirmReset && (
        <ConfirmDialog label="Reset the invite draft?" onClose={() => setConfirmReset(false)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Reset the invitation?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            Subject and body go back to the {templateName} template’s wording. The sender name is
            kept, and nothing changes until you save.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setConfirmReset(false)}
              disabled={resetting}
            >
              Keep my wording
            </button>
            <button type="button" className="btn btn--dark" onClick={handleReset} disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset'}
            </button>
          </div>
        </ConfirmDialog>
      )}

      {confirmArchive && (
        <ConfirmDialog
          label={space.archived_at ? 'Unarchive this space?' : 'Archive this space?'}
          onClose={() => setConfirmArchive(false)}
        >
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            {space.archived_at ? 'Unarchive this space?' : 'Archive this space?'}
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            {space.archived_at
              ? 'It returns to the active list, and the principal’s link works again.'
              : 'It moves to the Archived list. No staff member can start a call, the principal’s link stops opening it, and nothing new can be sent. Links and results are kept; you can unarchive at any time.'}
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setConfirmArchive(false)}
              disabled={archiving}
            >
              Cancel
            </button>
            <button type="button" className="btn btn--dark" onClick={handleArchive} disabled={archiving}>
              {archiving ? 'Working…' : space.archived_at ? 'Unarchive space' : 'Archive space'}
            </button>
          </div>
        </ConfirmDialog>
      )}

      {pendingNote && (
        <ConfirmDialog
          label="Replace the brief?"
          onClose={() => {
            setPendingNote(null)
            setPickedNote('')
          }}
        >
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Replace the brief?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            The brief field is replaced with what Claude writes from “{pendingNote.title}” —
            including anything typed and not yet saved. Nothing is saved until you save changes,
            and other unsaved changes on this page stay.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => {
                setPendingNote(null)
                setPickedNote('')
              }}
              disabled={importing}
            >
              Keep my brief
            </button>
            <button
              type="button"
              className="btn btn--dark"
              onClick={() => importNote(pendingNote)}
              disabled={importing}
            >
              {importing ? 'Importing…' : 'Replace brief'}
            </button>
          </div>
        </ConfirmDialog>
      )}
    </form>
  )
}
